/**
 * The Malting Company of Ireland — Togher, Cork.
 *
 * MCI publishes a full technical data sheet per malt: a moisture ceiling, a
 * fine-grind extract minimum, a diastatic power minimum in degrees
 * Windisch–Kolbach, and a wort colour band given in both °EBC and °Lovibond.
 * Every base malt here carries both the extract and the diastatic power this
 * catalog requires for `spec-sheet`, and all five publish both — so all five
 * rows are `spec-sheet`.
 *
 * `colorLovibond` is the mid-point of the maker's own Lovibond band (not the
 * °EBC band alongside it, which is IOB's own separate conversion); `colorEbc`
 * is then re-derived from that stored °L via this catalog's
 * `EBC = 2.65 × °L − 1.2`, so the two never disagree even where MCI's printed
 * EBC figure would round differently. `diastaticPowerLintner` converts MCI's
 * published WK minimum with `Lintner = (WK + 16) / 3.5`. Moisture and extract
 * are the maker's published ceiling and floor, taken as-is.
 *
 * @see https://www.maltingcompany.ie/our-malts/
 */

import type { Fermentable } from '../../schema/fermentable';

/** The Malting Company of Ireland fermentables. @see Fermentable */
export const irishFermentables: readonly Fermentable[] = [
  {
    id: 'mci-irish-ale-malt',
    name: 'Irish Ale Malt',
    maker: 'Malting Company of Ireland',
    origin: 'Ireland',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Irish two-row kilned to a warm golden-amber, a shade fuller and sweeter than a standard pale malt. Comfortable diastatic power converts itself with room to spare, so it can carry an Irish red or pale ale grist alone.',
    tags: ['base', 'pale-ale', 'irish'],
    url: 'https://www.maltingcompany.ie/our-malts/irish-ale-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'mci-irish-lager-malt',
    name: 'Irish Lager Malt',
    maker: 'Malting Company of Ireland',
    origin: 'Ireland',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 70.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Irish two-row kilned pale for a clean, sweet lager character. High diastatic power gives generous headroom for adjunct-heavy or high-gravity lager grists.',
    tags: ['base', 'lager', 'irish'],
    url: 'https://www.maltingcompany.ie/our-malts/irish-lager-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'mci-irish-stout-malt',
    name: 'Irish Stout Malt',
    maker: 'Malting Company of Ireland',
    origin: 'Ireland',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 81.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Irish two-row malted with heavy adjunct loads in mind: very high diastatic power to convert flaked barley alongside it and roasted barley on top, the way an Irish dry stout grist is built.',
    tags: ['base', 'stout', 'irish', 'high-enzyme'],
    url: 'https://www.maltingcompany.ie/our-malts/irish-stout-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'mci-irish-distilling-malt',
    name: 'Irish Distilling Malt',
    maker: 'Malting Company of Ireland',
    origin: 'Ireland',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Made for spirit yield rather than beer flavor, and the most enzymic malt in the range. Sold to Irish whiskey distillers for its fermentability, but it converts a beer mash just as readily.',
    tags: ['base', 'distilling', 'irish', 'high-enzyme'],
    url: 'https://www.maltingcompany.ie/our-malts/irish-distilling-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'mci-origin-gold',
    name: 'Origin Gold Low-Colour Base Malt',
    maker: 'Malting Company of Ireland',
    origin: 'Ireland',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 81.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The lightest malt MCI kilns, meant for pale, delicately hopped lagers and pale ales where the base malt should stay out of the way. High extract and diastatic power keep it usable across adjunct-forward brewing programs too.',
    tags: ['base', 'pilsner', 'irish'],
    url: 'https://www.maltingcompany.ie/our-malts/origin-gold-low-colour-base-malt/',
    provenance: 'spec-sheet',
  },
];
